// LifecycleTab — B7 (2026-08-18). The "Capability loop" tab in the Agent Detail
// Viewer. Presents the six-station lifecycle Template → Run → Evaluate → Improve
// → Promote → Observe as ONE connected journey, each station deep-linking to the
// REAL surface it lives on.
//
// THIN by policy: all routing logic lives in the unit-tested
// `capabilityLoopStages.js` (CAPABILITY_LOOP_STAGES + stageActions). This file is
// pure presentation + navigation wiring and is coverage-excluded (host shell).
import React from "react";
import { useNavigate } from "react-router-dom";
import { COLORS } from "../../../theme/colors";
import { CAPABILITY_LOOP_STAGES, stageActions } from "./capabilityLoopStages";

const RING = {
  template: COLORS.accent,
  run: COLORS.accent,
  evaluate: COLORS.orange,
  improve: COLORS.purple,
  promote: COLORS.purple,
  observe: COLORS.green,
};

export default function LifecycleTab({ tree }) {
  const navigate = useNavigate();
  const agentId = tree?.agent?.id || null;

  return (
    <div style={{ maxWidth: 920 }}>
      <div style={{ marginBottom: 20 }}>
        <div style={{ fontSize: 15, fontWeight: 700, color: COLORS.text }}>
          The capability loop
        </div>
        <div style={{ fontSize: 12.5, color: COLORS.textDim, marginTop: 6, lineHeight: 1.55 }}>
          Building a good agent is never one-and-done: you start from a template,
          run it against real traffic, judge what it got right and wrong, make a
          change, ship the better version, and watch what happens — which tells
          you what to change next. Each station below links to the real surface
          where that work happens.{" "}
          <a href="/help/core-capability-loop.html" target="_blank" rel="noreferrer"
            style={{ color: COLORS.accentText || COLORS.accent }}>
            Read the full guide →
          </a>
        </div>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        {CAPABILITY_LOOP_STAGES.map((s) => {
          const ring = RING[s.key] || COLORS.accent;
          const actions = stageActions(s.key, agentId);
          return (
            <div key={s.key} style={{
              border: `1px solid ${COLORS.border}`,
              borderLeft: `4px solid ${ring}`,
              borderRadius: 10, background: COLORS.surface,
              padding: "14px 18px",
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                <span style={{
                  display: "inline-flex", alignItems: "center", justifyContent: "center",
                  width: 22, height: 22, borderRadius: "50%",
                  background: `${ring}22`, color: ring, fontSize: 11, fontWeight: 700,
                }}>{s.index}</span>
                <span style={{ fontSize: 14, fontWeight: 700, color: COLORS.text }}>{s.title}</span>
                <span style={{ fontSize: 11, color: COLORS.textDim }}>{s.surface}</span>
                {s.honest && (
                  <span style={{
                    padding: "2px 8px", borderRadius: 6, fontSize: 9.5, fontWeight: 700,
                    background: `${COLORS.orange}22`, color: COLORS.orange,
                    border: `1px solid ${COLORS.orange}44`,
                    letterSpacing: "0.04em", textTransform: "uppercase",
                  }}>4 surfaces, not 1 button</span>
                )}
              </div>

              <div style={{ fontSize: 12.5, color: COLORS.textDim, margin: "8px 0 12px", lineHeight: 1.55 }}>
                {s.description}
              </div>

              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                {actions.map((a) => (
                  <button
                    key={a.label}
                    onClick={() => navigate(a.route)}
                    title={a.note || `Go to ${a.label}`}
                    style={{
                      background: "transparent",
                      border: `1px solid ${ring}66`,
                      color: COLORS.text, padding: "6px 12px", borderRadius: 8,
                      cursor: "pointer", fontSize: 11.5, fontWeight: 600,
                    }}
                  >{a.label} →</button>
                ))}
              </div>

              {s.actions.some((a) => a.note) && (
                <ul style={{ margin: "10px 0 0", padding: "0 0 0 16px" }}>
                  {s.actions.filter((a) => a.note).map((a) => (
                    <li key={a.label} style={{ fontSize: 11, color: COLORS.textDim, lineHeight: 1.5 }}>
                      <strong style={{ color: COLORS.textMuted || COLORS.text }}>{a.label}:</strong> {a.note}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
